import { SVG } from "@svgdotjs/svg.js";

type BuiltSvg = { svg: string; wPx: number; hPx: number };
export type ArrowSide = "front" | "back" | "left" | "right";

export function buildTopPreview(
    roomLengthMm: number,
    roomDepthMm: number,
    targetW: number,
    targetH: number,
    arrow?: ArrowSide,
    preferRotation: "auto" | "cw" | "none" = "auto"
): BuiltSvg {
    const pad = 6;
    const areaW = Math.max(0, targetW - 2 * pad);
    const areaH = Math.max(0, targetH - 2 * pad);

    const scaleUnrot = Math.min(areaW / Math.max(roomLengthMm, 1e-6), areaH / Math.max(roomDepthMm, 1e-6));
    const scaleCW = Math.min(areaW / Math.max(roomDepthMm, 1e-6), areaH / Math.max(roomLengthMm, 1e-6));

    const useCW = preferRotation === "cw" || (preferRotation === "auto" && scaleCW > scaleUnrot);

    const svg = SVG().size(targetW, targetH);
    svg.viewbox(0, 0, targetW, targetH);

    const rectW = (useCW ? roomDepthMm : roomLengthMm) * (useCW ? scaleCW : scaleUnrot);
    const rectH = (useCW ? roomLengthMm : roomDepthMm) * (useCW ? scaleCW : scaleUnrot);

    const x = pad + (areaW - rectW) / 2;
    const y = pad + (areaH - rectH) / 2;

    const wallStroke = 1;
    svg.rect(rectW, rectH).move(x, y).fill("none").stroke({ width: wallStroke, color: "#535353" });

    const fontPx = Math.max(7, Math.min(12, Math.min(rectW, rectH) * 0.18));
    const baseInset = wallStroke + 4;

    const mapCW = (s: ArrowSide): ArrowSide =>
        s === "front" ? "right" : s === "right" ? "back" : s === "back" ? "left" : "front";

    const insetLbl = Math.max(4, Math.min(12, Math.min(rectW, rectH) * 0.08));
    const centersByDisplayedSide: Record<ArrowSide, { cx: number; cy: number }> = {
        front: { cx: x + rectW / 2, cy: y + insetLbl },
        right: { cx: x + rectW - insetLbl, cy: y + rectH / 2 },
        back: { cx: x + rectW / 2, cy: y + rectH - insetLbl },
        left: { cx: x + insetLbl, cy: y + rectH / 2 },
    };

    if (arrow) {
        const displayedSide = useCW ? mapCW(arrow) : arrow;
        const labelClear = Math.max(4, fontPx * 0.6 + 2);

        const cx = x + rectW / 2;
        const cy = y + rectH / 2;

        let tx = cx,
            ty = cy,
            dx = 0,
            dy = 0;
        if (displayedSide === "front") {
            ty = y + baseInset + labelClear;
            dy = -1;
        }
        if (displayedSide === "back") {
            ty = y + rectH - baseInset - labelClear;
            dy = 1;
        }
        if (displayedSide === "left") {
            tx = x + baseInset + labelClear;
            dx = -1;
        }
        if (displayedSide === "right") {
            tx = x + rectW - baseInset - labelClear;
            dx = 1;
        }

        const fullLen = Math.hypot(tx - cx, ty - cy);
        let head = Math.min(Math.min(10, Math.max(6, Math.min(rectW, rectH) * 0.1)), Math.max(4, fullLen * 0.35));
        if (fullLen <= head + 1) head = Math.max(2, fullLen * 0.8);

        const bodyEndX = tx - dx * head;
        const bodyEndY = ty - dy * head;

        svg.line(cx, cy, bodyEndX, bodyEndY).stroke({ width: 1.2, color: "#a64022", linecap: "round" });

        const ortho = 0.5 * head;
        const p1 = [tx, ty];
        const p2 = [bodyEndX + -dy * ortho, bodyEndY + dx * ortho];
        const p3 = [bodyEndX + dy * ortho, bodyEndY + -dx * ortho];
        svg.polygon([p1, p2, p3] as any).fill("#a64022");
    }

    const letterMap: Record<ArrowSide, string> = {
        front: "A",
        right: "B",
        back: "C",
        left: "D",
    };

    if (arrow) {
        const displayedSide = useCW ? mapCW(arrow) : arrow;
        const { cx, cy } = centersByDisplayedSide[displayedSide];
        svg.text(letterMap[arrow]).font({ size: fontPx, family: "system-ui" }).fill("#a64022").center(cx, cy);
    } else {
        (["front", "right", "back", "left"] as ArrowSide[]).forEach((realSide) => {
            const displayedSide = useCW ? mapCW(realSide) : realSide;
            const { cx, cy } = centersByDisplayedSide[displayedSide];
            svg.text(letterMap[realSide]).font({ size: fontPx, family: "system-ui" }).fill("#a64022").center(cx, cy);
        });
    }

    return { svg: svg.svg(), wPx: targetW, hPx: targetH };
}
